import { copy as sharedUiCopy } from '@shared/copy';
import { Skeleton, SkeletonMetaRows } from '@shared/ui/Skeleton';

/**
 * The pending state of `/pages/about`.
 *
 * Three `PageSection`s and the action row that closes the form, in the order
 * the loaded surface draws them: the page's identity facts, the lede field, and
 * the body block editor.
 *
 * The identity rows are drawn WITHOUT a panel, because `PageIdentity` renders a
 * bare `MetaGrid`: a bordered card here would put a box on the surface and then
 * take it away.
 *
 * The body block is not a filled rectangle. The editor is a run of blocks, each
 * with its own handle row, and drawing it as one grey slab of the editor's
 * height is what made this the least informative placeholder on the host — the
 * tallest block on the page said the least about what was coming.
 */
function SectionHeadSkeleton() {
  return (
    <div className="mb-4">
      <Skeleton className="h-7 w-[10rem]" />
    </div>
  );
}

function BlockSkeleton() {
  return (
    <div className="grid gap-2 rounded-md border border-border bg-surface p-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-[5rem] rounded-sm" />
        <Skeleton className="ms-auto h-6 w-6 rounded-sm" />
        <Skeleton className="h-6 w-6 rounded-sm" />
        <Skeleton className="h-6 w-6 rounded-sm" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

export function AboutSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        {sharedUiCopy.state.loading}
      </p>

      <section aria-hidden="true">
        <SectionHeadSkeleton />
        <SkeletonMetaRows rows={3} />
      </section>

      <section aria-hidden="true" className="mt-section-gap">
        <SectionHeadSkeleton />
        <Skeleton className="mb-4 h-3 w-2/3 max-w-measure" />

        <div className="mb-4">
          <Skeleton className="mb-2 h-4 w-[7rem]" />
          {/* `rows={2}` at the control's own padding. */}
          <Skeleton className="h-[5rem] w-full rounded-sm" />
          <Skeleton className="mt-2 h-3 w-1/2" />
        </div>
      </section>

      <section aria-hidden="true" className="mt-section-gap">
        <SectionHeadSkeleton />
        <Skeleton className="mb-4 h-3 w-2/3 max-w-measure" />

        <div className="grid gap-3">
          <BlockSkeleton />
          <BlockSkeleton />
        </div>
      </section>

      <div
        aria-hidden="true"
        className="mt-4 flex flex-wrap gap-3 lg:mt-8 lg:border-t lg:border-border lg:pt-5"
      >
        <Skeleton className="h-[2.75rem] w-[7rem] rounded-sm" />
        <Skeleton className="h-[2.75rem] w-[6rem] rounded-sm" />
      </div>
    </>
  );
}
